
import React, { useState, useRef, useEffect } from "react";
import "../EditProfile/EditProfile.scss";
import UserImg from "../../../assets/images/vactor/default-image.svg";
import CameraImgIcon from "../../../assets/images/vactor/white-camera.svg";
import EditTextIcon from "../../../assets/images/vactor/edit-text-field.svg";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { LightButton, PrimaryButton } from "../../AllButtons/AllButtons";
import { useAppSelector, useAppDispatch } from "../../../Store/store";
import { updateAdmin } from "../../../Services/admin.service";
import { getUserDetails } from "../../../Services/profile.service";
import { uploadProfileImage } from "../../../Services/profile.service";
import { appendCacheBuster, resolveAssetUrl } from "../../../Utils/url";

interface EditProfileProps {
  open: boolean;
  onClose: () => void;
}

function EditProfile({ open, onClose }: EditProfileProps) {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user.user);
  
  // Initialize states properly
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user.name || "");
  const [tempName, setTempName] = useState(user.name || "");
  const [isLoading, setIsLoading] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  
  const textFieldRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [UserImage, setUserImage] = useState<string>(
    user.profile_image
      ? resolveAssetUrl(user.profile_image)
      : UserImg
  );

  // Update local state when user data changes
  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setTempName(user.name || "");
      const imageUrl = user.profile_image 
        ? appendCacheBuster(user.profile_image, Date.now())
        : UserImg;
      setUserImage(imageUrl);
    }
  }, [user]);

  const handleEditClick = () => {
    setIsEditing(true);
    setTempName(name);
    
    setTimeout(() => {
      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
    }, 0);
  };
  
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTempName(event.target.value);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      
      const allowedExtensions = ['jpg', 'jpeg', 'png'];
      
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        alert(`Invalid file type. Please select only JPG, JPEG, or PNG files.\n\nFile type detected: ${fileExtension?.toUpperCase() || 'Unknown'}`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      const allowedMimeTypes = ['image/jpeg', 'image/png'];
      if (!allowedMimeTypes.includes(file.type)) {
        alert(`Invalid file format. Please select only JPG, JPEG, or PNG files.\n\nDetected format: ${file.type}`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('Please select an image smaller than 5MB');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setSelectedImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setUserImage(previewUrl);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImageFile) return;

    setIsImageUploading(true);
    
    try {
      await dispatch(uploadProfileImage({
        userId: localStorage.getItem('id') || user.id,
        imageFile: selectedImageFile
      })).unwrap();

      // Refresh user details to get updated profile image
      await dispatch(getUserDetails(localStorage.getItem('id')));
      
      setSelectedImageFile(null);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error: any) {
      console.error('Failed to upload profile image:', error);
      
      // Revert to original image on error
      const originalImage = user.profile_image 
        ? resolveAssetUrl(user.profile_image) 
        : UserImg;
      setUserImage(originalImage);
      setSelectedImageFile(null);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      let errorMessage = 'Failed to upload image. Please try again.';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      alert(errorMessage);
    } finally {
      setIsImageUploading(false);
    }
  };
  
  const handleSave = async () => {
    setIsLoading(true);
    
    try {
      // First upload image if there's a selected file
      if (selectedImageFile) {
        await handleImageUpload();
      }

      // Then update name if it has changed
      if (tempName !== name) {
        const requestBodyForNameChange = {
          user_id: localStorage.getItem('id'),
          name: tempName, 
          email: user.email,
          grade: user.grade,
          birth_date: user.birth_date,
          is_paid: user.is_paid,
        };
        
        await dispatch(updateAdmin({
          user_id: localStorage.getItem('id') || user.id,
          requestBody: requestBodyForNameChange
        })).unwrap();

        await dispatch(getUserDetails(localStorage.getItem('id')));
        
        setName(tempName);
        setIsEditing(false);
        
      } else {
        setIsEditing(false);
      }
      
      // Close dialog after successful save
      onClose();
      
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setTempName(name);
    setIsEditing(false);
    
    if (selectedImageFile) {
      const originalImage = user.profile_image 
        ? resolveAssetUrl(user.profile_image) 
        : UserImg;
      setUserImage(originalImage);
      setSelectedImageFile(null);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    
    onClose();
  };
  
  return (
    <>
      <Dialog
        className="edit-profile-dialog"
        open={open}
        onClose={handleCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          className="edit-profile-dialog-title"
          id="alert-dialog-title"
        >
          {"Your Profile"}
        </DialogTitle>
        <DialogContent className="edit-profile-dialog-body">
          <div className="user-profile-block">
            <div className="user-img">
              <img src={UserImage} alt="" />
              <label 
                className="camera-icon-img" 
                htmlFor="UserProfile"
                style={{ 
                  cursor: (isLoading || isImageUploading) ? 'not-allowed' : 'pointer',
                  opacity: (isLoading || isImageUploading) ? 0.6 : 1 
                }}
              >
                <img src={CameraImgIcon} alt="" />
                <input 
                  id="UserProfile" 
                  type="file" 
                  hidden 
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={isLoading || isImageUploading}
                />
              </label>
            </div>
            {/* {selectedImageFile && (
              <div style={{ 
                marginTop: '10px', 
                textAlign: 'center', 
                fontSize: '12px', 
                color: '#666' 
              }}>
                Image selected: {selectedImageFile.name}
              </div>
            )} */}
            {/* {isImageUploading && (
              <div style={{ 
                marginTop: '5px', 
                textAlign: 'center', 
                fontSize: '12px', 
                color: '#007bff' 
              }}>
              </div>
            )} */}
          </div>
          <div className="user-name-edit-field">
            <div
              className="input-form-field-wrapper"
              style={{ display: "flex", flexDirection: "column" }}
            >
              <TextField
                className="out-label-input-text"
                id="name"
                name="name"
                placeholder="Name"
                margin="normal"
                variant="outlined"
                fullWidth
                type="text"
                value={isEditing ? tempName : name} // ✅ Fixed: Use correct state values
                onChange={handleChange}
                inputRef={textFieldRef}
                disabled={isLoading || isImageUploading}
                InputProps={{
                  readOnly: !isEditing,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        edge="end" 
                        aria-label="edit" 
                        onClick={handleEditClick}
                        disabled={isLoading || isImageUploading}
                      >
                        <img src={EditTextIcon} alt="Edit" style={{ width: 20, height: 20 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions className="edit-profile-dialog-footer">
          <PrimaryButton 
            onClick={handleSave}
            disabled={isLoading || isImageUploading}
          >
            Save Changes{/* {isLoading || isImageUploading ? 'Saving...' : 'Save Changes'} */}
          </PrimaryButton>
          <LightButton 
            onClick={handleCancel} 
            disabled={isLoading || isImageUploading}
          >
            Cancel
          </LightButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EditProfile;
